import { buildQuizQuestionsPayload } from '@helpers/quiz-analytics';
import { AnswerMap, QUIZ_TYPE, StepAnswer } from '@models/quiz';
import { useAnalyticsStore } from '@stores/analytics';
import { useQuizStore } from '@stores/quiz';
import { useUserStore } from '@stores/user';
import { storeToRefs } from 'pinia';
import { computed, nextTick, watch } from 'vue';

type QuizHistoryStorage = {
  counter: number;
};

const getStepAnswers = (answerMap: AnswerMap, stepId: string | number) => (
  answerMap[stepId] ?? answerMap[String(stepId)]
);

const answersChanged = (
  nextAnswers?: StepAnswer[],
  previousAnswers?: StepAnswer[],
) => JSON.stringify(nextAnswers ?? null) !== JSON.stringify(previousAnswers ?? null);

/**
 * Quiz analytics triggers aligned with Kinsyn (KIN-37 / KS-1022) for QuizStarted and
 * QuizStepCompleted. JRNYS-specific: QuizCompleted fires on landing a step flagged with
 * isQuizCompleteEvent in Craft (Kinsyn fires QuizCompleted on backend submission complete).
 *
 * @see https://jrnys.atlassian.net/browse/KIN-37
 */
export const useQuizAnalytics = (quizType: QUIZ_TYPE) => {
  const userStore = useUserStore();
  const quizStore = useQuizStore();
  const analyticsStore = useAnalyticsStore();

  const isGuest = computed(() => userStore.info?.isGuest ?? true);

  const {
    quizId,
    quizName,
    quizCategory,
    answers,
    quizResponse,
    currentStep,
  } = storeToRefs(quizStore);

  let trackStepAnalytics = false;

  watch(
    quizResponse,
    async (response) => {
      if (!response) {
        return;
      }

      await nextTick();
      trackStepAnalytics = true;
    },
    { immediate: true },
  );

  watch(
    answers,
    (newAnswers, oldAnswers) => {
      if (!trackStepAnalytics || !quizId.value || !quizName.value || !quizCategory.value) {
        return;
      }

      const previousAnswers = oldAnswers ?? {};
      const stepIds = new Set([
        ...Object.keys(newAnswers),
        ...Object.keys(previousAnswers),
      ]);

      for (const stepId of stepIds) {
        const stepAnswers = getStepAnswers(newAnswers, stepId);
        if (!stepAnswers?.length) {
          continue;
        }

        if (!answersChanged(stepAnswers, getStepAnswers(previousAnswers, stepId))) {
          continue;
        }

        const step = quizStore.permittedSteps.find(
          (quizStep) => String(quizStep.id) === String(stepId),
        );
        if (!step) {
          continue;
        }

        const stepIndex = quizStore.permittedSteps.findIndex(
          (quizStep) => String(quizStep.id) === String(stepId),
        );

        analyticsStore.trackQuizStepCompleted({
          quiz_name: quizName.value,
          quiz_category: quizCategory.value,
          quiz_id: String(quizId.value),
          quiz_step: stepIndex >= 0 ? stepIndex : 0,
          quiz_questions: buildQuizQuestionsPayload(step.answers, stepAnswers),
        });
      }
    },
    { deep: true },
  );

  watch(
    currentStep,
    (step, previousStep) => {
      if (!trackStepAnalytics || !step?.isQuizCompleteEvent) {
        return;
      }

      if (!quizId.value || !quizName.value || !quizCategory.value) {
        return;
      }

      if (previousStep?.id === step.id) {
        return;
      }

      analyticsStore.trackQuizCompleted({
        quiz_name: quizName.value,
        quiz_category: quizCategory.value,
        quiz_id: String(quizId.value),
      });
    },
  );

  watch(
    currentStep,
    (step) => {
      if(!step || !quizId.value || !quizName.value || !quizCategory.value) {
        return;
      }

      const stepIndex = quizStore.permittedSteps.findIndex(
        (quizStep) => String(quizStep.id) === String(step.id),
      );

      analyticsStore.trackViewQuizStep({
        quiz_name: quizName.value,
        quiz_category: quizCategory.value,
        quiz_id: String(quizId.value),
        stepId: step.id,
        stepSlug: step.slug,
        stepName: step.title,
        quiz_step: stepIndex >= 0 ? stepIndex : 0,
        quiz_questions: buildQuizQuestionsPayload(step.answers, [] as StepAnswer<any>[]),
      });
    },
    { immediate: true },
  );

  watch(
    quizResponse,
    (newQuizResponse) => {
      if (!quizId.value || !quizCategory.value || !quizName.value || !newQuizResponse) {
        return;
      }

      const trackStarted = () => {
        analyticsStore.viewContent(quizName.value);
        analyticsStore.trackQuizStarted({
          quiz_name: quizName.value,
          quiz_category: quizCategory.value,
          quiz_id: String(quizId.value),
        });
      };

      if (isGuest.value) {
        const item = localStorage.getItem(quizType);
        if (!item) {
          return;
        }

        const quizHistory: QuizHistoryStorage = JSON.parse(item);

        if (quizHistory.counter === 0) {
          trackStarted();
        }
      } else if (!newQuizResponse.submissionId) {
        trackStarted();
      }
    },
    { immediate: true },
  );
};
